import { LateralModal } from "@/components/modals/LateralModal";
import {
  ContactsOutlined,
  LockOutlined,
  ShopOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import {
  Typography,
  Flex,
  Button,
  Modal,
  Select,
  message,
  Spin,
  Divider,
} from "antd";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionSelectRow } from "@/components/permissions-lateral/permissions-selectRow";
import { StoreList } from "@/components/drag-list";
import { SellerIcon } from "@/components";
import { PermissionsSelect } from "@/components/permissions-select";
import {
  API_URL_PERMISSIONS_PARTICIPATION,
  API_URL_QUOTES,
} from "@/services/XCONS_ENV";
import { axiosInstance } from "@/utilities";
import { NormalModal } from "@/components/modals/NormalModal";
import {
  filterOutOperators,
  useOperatorContacts,
} from "@/hooks/useOperatorContacts";
import { NIVEL } from "@/utilities/niveles-de-permiso";

const { Text } = Typography;

const PERMISSION_CONFIG = [
  { code: "quote", label: "Presupuesto", badge: false, strong: true },
];

const PERMISSION_OPTIONS = [
  { value: "admin", label: NIVEL.admin },
  { value: "editor", label: NIVEL.editor },
  { value: "viewer", label: NIVEL.viewer },
];

// Mirror of ViviendaVerde\MarketplaceQuote\Model\Wishlist::PERMISSION_*.
// PRIVATE(0) and EDITOR_REGISTER(4) are not exposed in the UI; we collapse
// them to "viewer" on read and reject them server-side on write.
const ACCESS_LEVEL_TO_VALUE: Record<number, string> = {
  0: "viewer",
  1: "viewer",
  2: "editor",
  3: "admin",
  4: "viewer",
};
const VALUE_TO_ACCESS_LEVEL: Record<string, number> = {
  viewer: 1,
  editor: 2,
  admin: 3,
};

type Permission = {
  id: number;
  userId: number;
  contactId: number;
  permissionCode: string;
  entityId: number;
  participationType: string;
  participationIdentifier: string;
  value: string;
  position: number;
};

type UserPermissions = {
  contactId: number;
  userId: number;
  participationType: "customer" | "store" | "seller";
  participationIdentifier: string;
  firstname?: string;
  lastname?: string;
  permissions: { [key: string]: Permission | undefined };
};

interface QuotePermissionsLateralProps {
  quoteId: number;
  storeName?: string;
  storeCode?: string;
  storeLink?: string;
  shareHash?: string;
  accessLevel?: number;
  initialParticipations?: any[];
  onChange?: () => void;
}

export const QuotePermissionsLateral = ({
  quoteId,
  storeName,
  storeCode,
  storeLink,
  shareHash,
  accessLevel,
  initialParticipations,
  onChange,
}: QuotePermissionsLateralProps) => {
  const [isLoadingChange, setIsLoadingChange] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [participants, setParticipants] = useState<UserPermissions[]>([]);
  const [draftChanges, setDraftChanges] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const operatorContactIds = useOperatorContacts();
  const [generalAccess, setGeneralAccess] = useState<string>(
    ACCESS_LEVEL_TO_VALUE[Number(accessLevel ?? 1)] ?? "viewer",
  );

  // Keep local state in sync if the parent reloads the quote and accessLevel
  // changes externally.
  useEffect(() => {
    setGeneralAccess(
      ACCESS_LEVEL_TO_VALUE[Number(accessLevel ?? 1)] ?? "viewer",
    );
  }, [accessLevel]);

  const handleGeneralAccessChange = async (value: string) => {
    const numeric = VALUE_TO_ACCESS_LEVEL[value];
    if (!numeric) return;
    const previous = generalAccess;
    setGeneralAccess(value);
    setIsLoadingChange(true);
    try {
      await axiosInstance.patch(`${API_URL_QUOTES}/${quoteId}`, {
        accessLevel: numeric,
      });
      message.success("Acceso general actualizado");
      onChange?.();
    } catch (error) {
      console.error("Error actualizando acceso general:", error);
      message.error("No se pudo actualizar el acceso general");
      setGeneralAccess(previous);
    } finally {
      setIsLoadingChange(false);
    }
  };

  const { t } = useTranslation();

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => setIsModalOpen(false);

  // Group by the unique tuple of `permission_participation`: same contactId
  // may have multiple rows (one as store, another as seller) — each must
  // surface as its own entry in the side panel.
  const buildGroupKey = (
    contactId: number,
    participationType: string,
    participationIdentifier: string,
  ) => `${contactId}::${participationType}::${participationIdentifier}`;

  const groupPermissions = (allPermissions: Permission[]): UserPermissions[] => {
    if (allPermissions.length === 0) return [];

    const grouped: Record<string, UserPermissions> = {};

    const visiblePermissions = filterOutOperators(
      allPermissions as any[],
      operatorContactIds,
    );

    visiblePermissions.forEach((perm: any) => {
      if (!perm) return;
      const cId = perm.contactId;
      const user = perm.contact?.user;
      const rawType = (perm.participationType ?? "").trim().toLowerCase();
      const normalizedType: "customer" | "store" | "seller" =
        rawType === "" || rawType === "customer"
          ? "customer"
          : (rawType as "store" | "seller");
      const identifier = perm.participationIdentifier || "";
      const key = buildGroupKey(cId, normalizedType, identifier);

      if (!grouped[key]) {
        grouped[key] = {
          contactId: cId,
          userId: user?.userId || perm.userId,
          participationType: normalizedType,
          participationIdentifier: identifier,
          firstname:
            user?.firstname || perm.firstname || "Desconocido",
          lastname: user?.lastname || perm.lastname || "",
          permissions: {},
        };
      }

      grouped[key].permissions[perm.permissionCode] = perm;
    });

    return Object.values(grouped).sort((a, b) => {
      const posA = a.permissions.quote?.position ?? 0;
      const posB = b.permissions.quote?.position ?? 0;
      return posA - posB;
    });
  };

  const fetchAndGroupPermissions = async (
    entityId: number,
  ): Promise<UserPermissions[]> => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `${API_URL_PERMISSIONS_PARTICIPATION}/entity/${entityId}?codes=quote`,
      );
      const grouped = res.data || {};
      const allPermissions: Permission[] = [...(grouped.quote || [])];
      return groupPermissions(allPermissions);
    } catch (error) {
      console.error("Error al obtener permisos:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialParticipations?.length) {
      setParticipants(groupPermissions(initialParticipations));
    } else if (quoteId) {
      fetchAndGroupPermissions(quoteId).then(setParticipants);
    }
    // Re-run when operatorContactIds resolves (initial render may have an
    // empty set while the request is in flight).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, operatorContactIds]);

  const handlePermissionChange = async (
    participant: UserPermissions,
    permissionCode: string,
    newValue: string,
  ) => {
    if (newValue === "remove") {
      const perm = participant?.permissions[permissionCode];
      if (!perm) return;
      Modal.confirm({
        title: "¿Estas seguro que deseas eliminar esta relación?",
        okText: "Eliminar",
        cancelText: "Cancelar",
        okType: "danger",
        onOk: async () => {
          try {
            // `quote.participations` (used as initialParticipations) maps to
            // QuoteCustomerEntity which doesn't expose the row's PK `id`, so
            // perm.id may be undefined. Look it up by composite key when
            // missing — the /entity endpoint uses UserParticipation which
            // does include id.
            let permId = Number(perm.id);
            if (!Number.isFinite(permId) || permId <= 0) {
              const res = await axiosInstance.get(
                `${API_URL_PERMISSIONS_PARTICIPATION}/entity/${quoteId}?codes=${permissionCode}`,
              );
              const list = (res.data?.[permissionCode] || []) as any[];
              const match = list.find(
                (r: any) =>
                  Number(r.contactId) === Number(participant!.contactId) &&
                  String(r.participationType ?? "") ===
                    (participant!.participationType === "customer"
                      ? ""
                      : participant!.participationType) &&
                  String(r.participationIdentifier ?? "") ===
                    String(participant!.participationIdentifier ?? ""),
              );
              permId = Number(match?.id);
            }
            if (!Number.isFinite(permId) || permId <= 0) {
              message.error("No se encontró el id de la participación.");
              return;
            }
            await axiosInstance.delete(
              `${API_URL_PERMISSIONS_PARTICIPATION}/${permId}`,
            );
            message.success("Participante eliminado");
            const updated = await fetchAndGroupPermissions(quoteId);
            setParticipants(updated);
            onChange?.();
          } catch (error: any) {
            console.error("Error al eliminar participante", error);
            if (error?.message === "You do not have permission") {
              message.error("No tienes permiso para eliminar este participante.");
            } else {
              message.error("Error al intentar eliminar el participante.");
            }
          }
        },
      });
      return;
    }

    // The draft key encodes the full participation tuple so two rows with the
    // same contactId (store + seller) keep independent drafts.
    const groupKey = buildGroupKey(
      participant.contactId,
      participant.participationType,
      participant.participationIdentifier,
    );
    const key = `${groupKey}||${permissionCode}`;
    const newChanges = { ...draftChanges, [key]: newValue };
    setDraftChanges(newChanges);
    handleSaveChanges(newChanges);
  };

  const handleAddParticipant = async (
    contact: any,
    permissions: { [code: string]: string },
  ) => {
    try {
      const permissionList = Object.entries(permissions)
        .filter(([, value]) => Boolean(value))
        .map(([code, value]) => ({ code, value }));

      if (!permissionList.length) {
        message.warning("Seleccioná al menos un permiso");
        return;
      }

      // Position is scoped to the (participationType) level — customer
      // owner is the customer with position=0, store/seller owners are
      // independent. Compute MAX+1 within the same level, but if nobody at
      // that level holds position=0 yet (ownerless level), grant it to the
      // newcomer.
      const newType: "customer" | "store" | "seller" =
        contact?.role === "customer" || !contact?.role ? "customer" : contact.role;
      const sameLevel = participants.filter(
        (p) => p.participationType === newType,
      );
      const hasOwnerAtLevel = sameLevel.some(
        (p) => (p.permissions.quote?.position ?? -1) === 0,
      );
      const maxPosAtLevel = sameLevel.reduce(
        (max, p) => Math.max(max, p.permissions.quote?.position ?? 0),
        -1,
      );
      const newPos = hasOwnerAtLevel ? maxPosAtLevel + 1 : 0;

      const basePayload = {
        /**
         * Un operador del store —o de un seller asociado— puede no tener ficha en este store: el
         * buscador lo devuelve con `contactId: null`. En ese caso se manda `userId` + `storeCode`
         * y el backend le crea la ficha al agregarlo.
         *
         * Lo que NO se hace es mandar la ficha que tenga en otro store: eso es exactamente el
         * daño que hay en la base — participaciones de presupuestos de un store registradas
         * contra fichas de otro.
         */
        contactId: contact.contactId || undefined,
        userId: contact.contactId ? undefined : (contact as any).id,
        storeCode,
        entityId: Number(quoteId),
        // Backend EntityType enum maps Customer to ''.
        participationType: newType === "customer" ? "" : newType,
        /**
         * El cargo lo define el rol elegido en el selector, no el store del presupuesto.
         *
         * Acá iba `storeCode` fijo: un operador del seller `tecnopor` agregado a un presupuesto
         * de `clickmateriales` se posteaba como `('seller', 'clickmateriales')`, o sea afirmando
         * un cargo que no tiene. Desde que el API valida el cargo declarado
         * (`assertDeclaredRoleIsReal`) eso muere con 422 `PARTICIPANT_ROLE_NOT_HELD`; antes se
         * guardaba mal y en silencio.
         *
         * `roleCode` es el código que el selector ya resolvió por opción (`_meta.code`): el seller
         * para un Seller Operator, el store para un Store Operator (el buscador acota los cargos
         * de store al store del contexto, así que ahí coincide con `storeCode`). El cliente no
         * declara cargo y conserva el store.
         */
        participationIdentifier:
          newType === "customer" ? storeCode : contact.roleCode || storeCode,
        position: newPos,
      };

      await Promise.all(
        permissionList.map(({ code, value }) =>
          axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, {
            ...basePayload,
            permissionCode: code,
            value,
          }),
        ),
      );

      const updated = await fetchAndGroupPermissions(quoteId);
      setParticipants(updated);
      handleCancel();
      onChange?.();
      message.success("Participante agregado");
    } catch (error) {
      console.error("Error al agregar participante:", error);
      message.error("Error al agregar participante");
    }
  };

  const handleUpdatePosition = async (newList: any[], type: string) => {
    setIsLoadingChange(true);
    try {
      const updates: any[] = [];
      // Backend EntityType enum maps Customer to ''. The drag-list categorizes
      // customers under the "customer" key, so we translate before POSTing.
      const backendType = type === "customer" ? "" : type;

      newList.forEach((item) => {
        const participant = item.raw.participant;
        const newPos = item.position;

        PERMISSION_CONFIG.forEach((conf) => {
          const perm = participant.permissions[conf.code];
          if (perm && perm.position !== newPos) {
            updates.push({
              contactId: participant.contactId,
              permissionCode: conf.code,
              entityId: Number(quoteId),
              participationType: backendType,
              participationIdentifier: participant.participationIdentifier,
              value: perm.value,
              position: newPos,
            });
          }
        });
      });

      if (updates.length > 0) {
        await Promise.all(
          updates.map((payload) =>
            axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, payload),
          ),
        );
        message.success("Posiciones actualizadas");
        const updated = await fetchAndGroupPermissions(quoteId);
        setParticipants(updated);
        onChange?.();
      }
    } catch (error) {
      console.error("Error actualizando posiciones:", error);
      message.error("Error al actualizar posiciones");
    } finally {
      setIsLoadingChange(false);
    }
  };

  const categorizedParticipants = useMemo(() => {
    const categories: Record<string, UserPermissions[]> = {
      customer: [],
      store: [],
      seller: [],
    };
    participants.forEach((p) => {
      if (categories[p.participationType]) {
        categories[p.participationType].push(p);
      }
    });
    return categories;
  }, [participants]);

  const categoryConfig = [
    { type: "customer", label: "Cliente", icon: <ContactsOutlined /> },
    { type: "store", label: "Store", icon: <ShopOutlined /> },
    { type: "seller", label: "Seller", icon: <SellerIcon /> },
  ];

  const handleSaveChanges = async (changes: Record<string, string>) => {
    if (Object.keys(changes).length === 0) return;

    setIsLoadingChange(true);
    try {
      await Promise.all(
        Object.entries(changes).map(async ([key, value]) => {
          const [groupKey, permissionCode] = key.split("||");
          const participant = participants.find(
            (p) =>
              buildGroupKey(
                p.contactId,
                p.participationType,
                p.participationIdentifier,
              ) === groupKey,
          );
          if (!participant) return;

          const payload = {
            contactId: participant.contactId,
            permissionCode,
            entityId: Number(quoteId),
            participationType:
              participant.participationType === "customer"
                ? ""
                : participant.participationType,
            participationIdentifier: participant.participationIdentifier,
            value,
            position: participant.permissions[permissionCode]?.position ?? 0,
          };
          await axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, payload);
        }),
      );

      message.success("Permisos actualizados");
      const updated = await fetchAndGroupPermissions(quoteId);
      setParticipants(updated);
      setDraftChanges({});
      onChange?.();
    } catch (error) {
      console.error("Error guardando permisos:", error);
      message.error("Error al guardar cambios");
    } finally {
      setIsLoadingChange(false);
    }
  };


  return (
    <LateralModal
      title={t("permissions")}
      buttonSize="small"
      buttonType="text"
      buttonName={t("permissions")}
      iconPosition="end"
      iconSvg={<LockOutlined />}
    >
      <Spin spinning={loading}>
        <NormalModal
          footer={null}
          destroyOnClose
          open={isModalOpen}
          onCancel={handleCancel}
          title="Agregar participante"
        >
          <PermissionsSelect
            handleCancel={handleCancel}
            onSaveContact={handleAddParticipant}
            store={
              storeCode
                ? { code: storeCode, name: storeName || "", link: "store" }
                : undefined
            }
            contactStores={{ store: { code: storeCode } } as any}
            permissions={[{ code: "quote", label: "Presupuesto" }]}
            excludedContactIds={participants.map((p) => p.contactId)}
          />
        </NormalModal>

        <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          <Flex vertical gap={12}>
          <Flex justify="space-between" align="center">
            <Flex gap={8} align="center">
              <ShopOutlined />
              <Text strong>{storeName}</Text>
            </Flex>
            <Button
              size="small"
              shape="round"
              icon={<UsergroupAddOutlined />}
              onClick={showModal}
            >
              {t("buttom.add")}
            </Button>
          </Flex>

          {participants.length === 0 && !loading && (
            <Text
              type="secondary"
              italic
              style={{ textAlign: "center", marginTop: 20 }}
            >
              {t("message.noParticipantsFound")}
            </Text>
          )}

          <Flex vertical gap={4}>
            {categoryConfig.map(({ type, label, icon }) => {
              const list = categorizedParticipants[type];
              if (list.length === 0) return null;

              return (
                <StoreList
                  key={type}
                  type={type}
                  icon={type === "seller"}
                  title={
                    <Flex align="center" gap={8}>
                      {icon}
                      <Text style={{ textTransform: "capitalize" }}>
                        {type === "seller" && list[0]?.participationIdentifier
                          ? `${label} ${list[0].participationIdentifier}`
                          : label}
                      </Text>
                    </Flex>
                  }
                  participants={list.map((p) => ({
                    id: buildGroupKey(
                      p.contactId,
                      p.participationType,
                      p.participationIdentifier,
                    ),
                    name: `${p.firstname} ${p.lastname}`,
                    position: p.permissions.quote?.position ?? 0,
                    raw: { participant: p },
                  }))}
                  hideSelect
                  getParticipants={() => fetchAndGroupPermissions(quoteId)}
                  onChange={(newList) => handleUpdatePosition(newList, type)}
                  renderHeader={(item) => {
                    const p = item.raw.participant;
                    const conf = PERMISSION_CONFIG[0];
                    const isOwner = item.position === 0;
                    return (
                      <PermissionSelectRow
                        key={conf.code}
                        badge={conf.badge}
                        strong={conf.strong}
                        label={
                          <Flex align="center" gap={6}>
                            <Text strong>{`${p.firstname} ${p.lastname}`}</Text>
                            {isOwner && (
                              <Text
                                type="secondary"
                                style={{ fontSize: "12px", fontWeight: "normal" }}
                              >
                                (Dueño)
                              </Text>
                            )}
                          </Flex>
                        }
                        value={p.permissions[conf.code]?.value}
                        customerMode={type === "customer"}
                        allowRemove={type !== "customer"}
                        onChange={(val) =>
                          handlePermissionChange(p, conf.code, val)
                        }
                        isLoadingChange={isLoadingChange}
                      />
                    );
                  }}
                />
              );
            })}
          </Flex>

          <Divider style={{ margin: "6px 0" }} />

          <Flex align="center" justify="space-between">
            <Flex vertical style={{ flex: 1, marginRight: 12 }}>
              <Text strong style={{ fontSize: 12 }}>
                Acceso general para clientes
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Cualquiera con el link tiene este permiso por defecto.
              </Text>
            </Flex>
            <Select
              size="small"
              value={generalAccess}
              onChange={handleGeneralAccessChange}
              style={{ width: 110 }}
              options={PERMISSION_OPTIONS}
              loading={isLoadingChange}
              disabled={isLoadingChange}
            />
          </Flex>
          </Flex>
          </div>
        </Flex>
      </Spin>
    </LateralModal>
  );
};
